import React, { useEffect, useState, useCallback } from "react";
import { Award } from "lucide-react";

// Tiny module-level event bus so non-React callers (axios, lib code) can
// dispatch unlocks. Components subscribe via subscribeUnlocks().
const listeners = new Set();
let queueId = 0;

export function pushUnlocks(items) {
  if (!items || !items.length) return;
  for (const item of items) {
    queueId += 1;
    listeners.forEach((fn) => fn({ ...item, _qid: queueId }));
  }
}

export function subscribeUnlocks(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const DISPLAY_MS = 2600;

/** Single banner card. Self-removes after DISPLAY_MS. */
function BannerCard({ item, onDone }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    // entrance
    const a = setTimeout(() => setVisible(true), 20);
    // exit
    const b = setTimeout(() => setVisible(false), DISPLAY_MS - 350);
    // unmount
    const c = setTimeout(() => onDone(item._qid), DISPLAY_MS);
    return () => { clearTimeout(a); clearTimeout(b); clearTimeout(c); };
  }, [item._qid, onDone]);

  return (
    <div
      data-testid={`achievement-banner-${item.key}`}
      className={`pointer-events-auto transition-all duration-300 ease-out
                  ${visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"}`}
      style={{
        background: "linear-gradient(180deg, #160404 0%, #0A0A0C 100%)",
        border: "1px solid #6A0000",
        boxShadow: "0 0 0 1px #2a0000, 0 12px 40px rgba(106,0,0,0.45)",
        fontFamily: "IBM Plex Mono, monospace",
        color: "#EAE6D7",
        minWidth: 320,
        maxWidth: 480,
      }}
    >
      <div className="flex items-start gap-3 px-4 py-3">
        <div className="mt-0.5 shrink-0" style={{ color: "#C49A4A" }}>
          <Award size={22} strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.25em] opacity-70" style={{ color: "#C49A4A" }}>
            ACHIEVEMENT UNLOCKED
          </div>
          <div className="text-base font-semibold mt-0.5 truncate" data-testid={`achievement-name-${item.key}`}>
            {item.name}
          </div>
          <div className="text-xs opacity-75 mt-0.5">{item.desc}</div>
          {item.title ? (
            <div className="text-[10px] mt-1.5 tracking-[0.18em]" style={{ color: "#C49A4A" }}>
              TITLE EARNED — {item.title.toUpperCase()}
            </div>
          ) : null}
        </div>
      </div>
      {/* progress bar */}
      <div className="h-[2px] w-full overflow-hidden" style={{ background: "#1a0606" }}>
        <div
          style={{
            height: "100%",
            background: "#C49A4A",
            animation: `achv-shrink ${DISPLAY_MS}ms linear forwards`,
          }}
        />
      </div>
    </div>
  );
}

export default function AchievementBanner() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const off = subscribeUnlocks((item) => {
      setItems((curr) => [...curr, item]);
    });
    return off;
  }, []);

  const remove = useCallback((qid) => {
    setItems((curr) => curr.filter((i) => i._qid !== qid));
  }, []);

  return (
    <>
      <style>{`@keyframes achv-shrink { from { width: 100%; } to { width: 0%; } }`}</style>
      <div
        data-testid="achievement-banner-stack"
        className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] flex flex-col gap-2 pointer-events-none"
      >
        {items.map((it) => (
          <BannerCard key={it._qid} item={it} onDone={remove} />
        ))}
      </div>
    </>
  );
}
